﻿<template>
    <div class="flex flex-column">

        <!-- layout='inline'（預設）：圖 / 輸入 / refresh / 語音 全部單行（login 既有版面，不變） -->
        <div v-if="layout === 'inline'" class="flex flex-row align-items-center justify-content-end">
            <div class="flex w-5rem h-2rem pr-1">
                <img :src="image" class="border-round-sm" style="width:100%; height:100%;" />
            </div>
            <div class="flex w-6rem pr-1">
                <InputText v-model="verify$.code.$model" :placeholder="$t('captcha.placeholder')" :class="{'p-invalid': verify$.code.$invalid }" class="w-6rem py-2" :maxLength="codeLength" style="text-align: center"></InputText>
            </div>
            <div class="flex pl-2 pr-1">
                <Button tabindex="-1" icon="fa-solid fa-arrows-rotate" class="p-button-rounded p-button-secondary active:text-red-500 h-2rem w-2rem" v-tooltip.top="$t('captcha.reloadCAPTCHA')" @click="loadCaptchaPicture()" />
            </div>
            <div class="flex pr-1" v-if="props.enabled">
                <Button tabindex="-1" icon="fa-solid fa-volume-high" class="p-button-rounded p-button-help active:text-red-500 h-2rem w-2rem" v-tooltip.top="$t('captcha.playCAPTCHA')" @click="onPlaySound" />
            </div>
        </div>

        <!-- layout='stacked'：第一行 圖 + 輸入；第二行 refresh + 語音 靠右 -->
        <template v-else>
            <!-- 第一行：圖 + 輸入（stacked 放大，整列寬度對齊下方驗證按鈕） -->
            <div class="flex flex-row align-items-center justify-content-center gap-2">
                <div class="flex" style="width:9rem; height:3rem;">
                    <img :src="image" class="border-round-sm" style="width:100%; height:100%;" />
                </div>
                <div class="flex">
                    <InputText v-model="verify$.code.$model" :placeholder="$t('captcha.placeholder')" :class="{'p-invalid': verify$.code.$invalid }" :maxLength="codeLength" style="width:7rem; height:3rem; text-align:center; font-size:1.25rem"></InputText>
                </div>
            </div>
            <!-- 第二行：refresh + 語音，靠右 -->
            <div class="flex flex-row align-items-center justify-content-end gap-2 mt-2">
                <Button tabindex="-1" icon="fa-solid fa-arrows-rotate" class="p-button-rounded p-button-secondary active:text-red-500 h-2rem w-2rem" v-tooltip.top="$t('captcha.reloadCAPTCHA')" @click="loadCaptchaPicture()" />
                <Button v-if="props.enabled" tabindex="-1" icon="fa-solid fa-volume-high" class="p-button-rounded p-button-help active:text-red-500 h-2rem w-2rem" v-tooltip.top="$t('captcha.playCAPTCHA')" @click="onPlaySound" />
            </div>
        </template>

        <!-- 錯誤訊息（兩種排版共用，置於最下） -->
        <div class="flex h-2rem justify-content-center">
            <div v-if="verify$.code.$error" class="p-error">{{ $t(`captcha.error.${verify$.code.$errors[0].$validator}`) }}</div>
        </div>
    </div>
</template>

<style scoped>
</style>

<script lang="ts">
    //normal <script> executed in module scope (only once)
    //只會執行一次(第1次) 除非按下F5

    export default {
        name: module$.captcha,
        inheritAttrs: false
    }
</script>

<script setup lang="ts">
    import { onMounted, onUnmounted, ref, WritableComputedRef, computed } from 'vue'
    import { utilfcty$ } from '../utils/utility'
    import { useI18n } from 'vue-i18n';
    import { module$, url$ } from '../enums'
    import { useVuelidate } from "@vuelidate/core";

    const props = defineProps({
        modelValue: { type: String, required: true, default: '' },
        captchaKey: { type: String, required: true, default: '' },
        // ── 通用化新增 ──
        captchaUrl: { type: String, default: url$.auth.concat('load-captcha') },  // 出題端點，預設 login
        enabled: { type: Boolean, default: true },                              // 啟用，預設 true
        codeLength: { type: Number, default: 4 },                                  // 碼長初始值（後端回應仍覆寫）
        // 版面：'inline'（單行，login 既有）｜'stacked'（兩行：圖+輸入一行、按鈕一行靠右）
        layout: { type: String, default: 'inline' }
    });
    const emit = defineEmits(['update:modelValue', 'update:captchaKey', 'update:codeLength']);

    //***************工具物件***************
    const util$ = utilfcty$.vueUse();
    //locales/{locales}/captcha.json
    const { t, locale } = useI18n(); // 取得翻譯函式 t

    //***************屬性/計算**************
    //codeLength（後端回應覆寫；以 v-model:codeLength 雙向同步給父層，使父層驗證用的碼長與後端一致，免寫死）
    const codeLength: WritableComputedRef<number> = computed({
        get(): number {
            return props.codeLength;
        },
        set(value: number) {
            emit('update:codeLength', value)
        }
    })
    //code
    const code: WritableComputedRef<string> = computed({
        get(): string {
            return props.modelValue;
        },
        set(value: string) {
            emit('update:modelValue', value)
        }
    })
    //key
    const captchaKey: WritableComputedRef<string> = computed({
        get(): string {
            return props.captchaKey;
        },
        set(value: string) {
            emit('update:captchaKey', value)
        }
    })
    const image = ref('');
    const sound = ref();
    // 驗證碼到期自動重載計時器（後端回 expirySeconds 才啟用）
    let reloadTimer: any = null;
    // 失敗背景重試計時器（自動觸發載入失敗時啟用，無限重試直到成功）
    let retryTimer: any = null;
    // 是否已記過「載入失敗」log：節點式記錄（第一次失敗記 warn、恢復記 info），中間每次重試不記，避免洗版
    let retryLogged = false;
    const RETRY_INTERVAL_SEC = 5;   // 背景重試間隔（秒）
    //檢查輸入格式
    const verifyRules = {
        code: {
            required: (value: string) => (!props.enabled || (value || '').length == codeLength.value)
        }
    };
    //($scope: false每個模組間不互相影響)
    const verify$ = useVuelidate(verifyRules, { code }, { $scope: false });

    //***************元件函式**************
    /** 載入驗證碼圖檔。
        @param silent 自動觸發（onMounted / 到期重載 / 背景重試）傳 true：失敗靜默（不彈 toast，由 http 的 config.silent 控制），
                      且失敗後啟動背景重試（每 RETRY_INTERVAL_SEC 秒，無限重試直到成功），server / 網路恢復後自動載回。
                      手動觸發（refresh 鈕）用預設 false：失敗照常彈 toast（使用者主動，需告知失敗），不啟動背景重試。
        失敗一律清空圖（catch）：使用者見空白即知 server / 網路有異常；背景重試成功後圖自動填回。 */
    const loadCaptchaPicture = async (silent = false) => {
        if (!props.enabled) return;

        interface CaptchaData {
            key: string;
            codeLength: number;
            base64Image: string;
            base64Sound: string;
            expirySeconds?: number;   // 後端回的過期秒數；到期前自動重載（後端未回則不自動重載，維持原行為）
        }
        await util$.http$.post<CaptchaData>(
            props.captchaUrl,
            { fontSize: util$.pinia$.app.styling.fontSize, locale: locale.value },
            silent ? ({ silent: true } as any) : undefined   // 自動觸發：靜默（http 攔截器依 config.silent 不彈 toast / log）
        )
            .then((response) => {
                // 成功：若先前曾失敗進入背景重試，記一次恢復（節點 log），並停止重試計時器
                if (retryLogged) {
                    util$.log$.info('[Captcha] 重試成功，已恢復');
                    retryLogged = false;
                }
                clearRetryTimer();
                captchaKey.value = response.key
                codeLength.value = response.codeLength;
                image.value = response.base64Image;   // 成功：直接替換新圖（不先清空，避免空白閃爍）
                sound.value = response.base64Sound;
                scheduleReload(response.expirySeconds);   // 依過期秒數設自動重載（換新圖+新過期時間）
            })
            .catch(() => {
                // 失敗：清空圖（使用者見空白即知連線異常）。已於此 catch 處理，不再往外拋（避免 unhandledrejection）。
                image.value = '';
                if (silent) {
                    // 自動觸發失敗 → 背景重試（無限，每 RETRY_INTERVAL_SEC 秒）。第一次失敗記一次 warn（節點 log），
                    // 之後每次重試不記，避免洗版；server / 網路恢復後 then 分支會記「已恢復」。
                    if (!retryLogged) {
                        util$.log$.warn('[Captcha] 載入失敗，背景重試中…');
                        retryLogged = true;
                    }
                    scheduleRetry();
                }
                // 手動觸發失敗：http 攔截器已彈 toast，不啟動背景重試（使用者可自行再按 refresh）。
            })
    }

    /** 背景重試：每 RETRY_INTERVAL_SEC 秒重試一次（silent，無限直到成功）。
        成功由 loadCaptchaPicture 的 then 分支停止（clearRetryTimer + 記恢復）。 */
    const scheduleRetry = () => {
        clearRetryTimer();
        retryTimer = setTimeout(() => {
            loadCaptchaPicture(true);   // 仍 silent：續行背景重試（失敗則再排下一次，成功則 then 停止）
        }, RETRY_INTERVAL_SEC * 1000);
    }
    const clearRetryTimer = () => {
        if (retryTimer) { clearTimeout(retryTimer); retryTimer = null }
    }

    /** 依過期秒數設自動重載：過期前 3 秒重載，避免邊界（剛好到期時送出已逾時）。
        後端未回 expirySeconds（或 <=0）則不自動重載，維持既有行為（不影響未提供此欄位的端點，如舊版 login）。 */
    const scheduleReload = (expirySeconds?: number) => {
        clearReloadTimer();
        if (!expirySeconds || expirySeconds <= 0) return;
        const delaySec = Math.max(expirySeconds - 3, 1);   // 提前 3 秒；至少 1 秒
        reloadTimer = setTimeout(() => {
            verify$.value.$reset();          // 清驗證狀態殘留（清紅字；code 不清空，由使用者自行察覺）
            loadCaptchaPicture(true);   // 到期重載（自動觸發 → silent：失敗清空圖 + 背景重試，恢復後自動載回）
        }, delaySec * 1000);
    }
    const clearReloadTimer = () => {
        if (reloadTimer) { clearTimeout(reloadTimer); reloadTimer = null }
    }
    const onPlaySound = () => {
        const snd = new Audio(sound.value);
        snd.play().catch((err) => {
            // 手機自動播放策略可能擋下（NotAllowedError）或格式不支援（NotSupportedError）；
            // 記 warn 供診斷，靜默不中斷（此非 axios 錯誤，全域 unhandledrejection 監聽不接）。
            util$.log$.warn('[Captcha] 播放驗證碼語音失敗:', err?.name);
        });
    }
    //***************元件監看**************

    //***************元件鉤子**************
    onMounted(() => {
        loadCaptchaPicture(true);   // 初次載入（自動觸發 → silent：失敗靜默 + 清空圖 + 背景重試，恢復後自動載回）
    })

    onUnmounted(() => {
        clearReloadTimer();
        clearRetryTimer();   // 離開頁面停止背景重試，避免持續打請求
    })

    defineExpose({
        loadCaptchaPicture
    })

</script>